import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useFetchAddressSuggestinsQuery } from "../../store";
import { twMerge } from "tailwind-merge";
import { AddressSuggestion } from "../../types";
import { FaMapMarkerAlt } from "react-icons/fa";
import Skeleton from "../common/Skeleton";
import { useDebounce } from "../../hooks";

interface SearchBarProps {
  value?: AddressSuggestion;
  placeholder?: string;
  className?: string;
  setAddress: (value: AddressSuggestion) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  placeholder,
  className,
  setAddress,
}) => {
  // hooks
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchText, 400);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("click", handler, false);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm.length > 1) setIsOpen(true);
    else setIsOpen(false);
  }, [debouncedSearchTerm]);

  // queries & mutations
  const { data: adresses, isFetching: isFetchingAdresses } =
    useFetchAddressSuggestinsQuery(debouncedSearchTerm, {
      skip: debouncedSearchTerm.length < 2,
    });

  // events
  const handleOnChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setSearchText(e.target.value);
  };

  const handleOptionClick = (option: AddressSuggestion) => {
    setIsOpen(false);
    setSearchText("");
    setAddress(option);
  };

  // classes
  const wrapperClasses = classNames(
    "w-full relative flex flex-col justify-center shadow-lg",
    className
  );

  const inputContainerClasses = twMerge(
    classNames("w-full relative flex flex-col rounded justify-center p-2")
  );
  const inputClasses = classNames("w-full text-lg focus:outline-none");

  // renders
  const renderedOptions = adresses?.map((address) => {
    return (
      <div
        key={address.placeId}
        className="hover:bg-gray-200 rounded cursor-pointer p-1"
        onClick={() => handleOptionClick(address)}
      >
        {address.displayName}
      </div>
    );
  });

  return (
    <div className={wrapperClasses}>
      <div ref={divEl} className={inputContainerClasses}>
        <div className="flex items-center gap-2">
          <FaMapMarkerAlt className="text-primary text-lg" />
          <input
            value={
              isFocused ? searchText : searchText || value?.displayName || ""
            }
            placeholder={placeholder}
            className={inputClasses}
            onChange={handleOnChange}
            autoComplete="off"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full w-full border bg-white z-[100] overflow-y-auto max-h-60 p-2">
          {isFetchingAdresses ? (
            <Skeleton className="h-10 w-full" times={3} />
          ) : renderedOptions && renderedOptions.length ? (
            renderedOptions
          ) : (
            <div className="flex flex-col rounded">
              <div>Aucune adresse trouvée</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
